import { Button, FormGroup, Input, Label } from "reactstrap";
import { useThemeContext } from "../../../Context/ThemeContext";
import { getDarkInput } from "../../../Utils/otherFunctions";
import { forwardRef, useState, useRef, useEffect } from "react";

const ImageInputField = forwardRef(function FileInputField(props, ref) {
  const {
    label,
    className,
    fieldState,
    placeholder,
    defaultPreview,
    maxSize = 5,
    onChange,
    name,
    isRequired,
    accept,
  } = props;
  const { themeContext } = useThemeContext();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [fileChanged, setFileChanged] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (defaultPreview) {
      setPreview(defaultPreview);
    }
  }, [defaultPreview]);

  const validateFileSize = (file) => {
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      return `Ukuran gambar maksimal ${maxSize}MB`;
    }
    return null;
  };

  const handleImageChange = (file) => {
    if (file) {
      const sizeError = validateFileSize(file);
      if (sizeError) {
        setError(sizeError);
        setPreview(null);
        setFileChanged(false); 
        return;
      }

      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setFileChanged(true);
      onChange(file); 
    } else {
      setPreview(null);
      setError(null);
      setFileChanged(false); 
      onChange(null);
    }
  };

  const handleDeleteImage = () => {
    setPreview(null);
    setError(null);
    setFileChanged(true); 
    onChange(null); 
  };

  const handlePreviewClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleImageChange(file);
    }
  };

  return (
    <FormGroup className={className}>
      {label && (
        <Label className="bg-transparent">
          {label}{" "}
          {isRequired && (
            <font className="ml-1" color="red">
              *
            </font>
          )}
        </Label>
      )}

      <div
        className="position-relative"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {preview ? (
          <div
            className="preview-container cursor-pointer"
            onClick={handlePreviewClick}
            style={{
              width: "100%",
              minHeight: "200px",
              border: "2px dashed black",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "10px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Button
              onClick={() => handleDeleteImage()}
              className="text-dark btn rounded p-1"
              color="light"
              outline
              style={{
                position: "absolute",
                left: "90%",
                bottom: "82%",
                zIndex: "9999",
              }}
            >
              x
            </Button>
            <img
              src={preview}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                objectFit: "contain",
              }}
            />
            <div
              className="preview-overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.3)",
                opacity: 0,
                transition: "opacity 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "14px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
            >
              Klik untuk mengganti gambar
            </div>
          </div>
        ) : (
          <div
            className="upload-placeholder"
            onClick={handlePreviewClick}
            style={{
              width: "100%",
              height: "200px",
              border: "1px dashed black",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "10px",
              cursor: "pointer",
              background: getDarkInput(themeContext),
            }}
          >
            <div className="text-center">
              <div>Klik atau drag & drop untuk upload gambar</div>
              <small className="text-muted">
                {accept
                  ? `Format yang didukung: ${accept}`
                  : "Semua format gambar didukung"}
              </small>
              <br />
              <small className="text-muted">
                Maksimal ukuran file: {maxSize}MB
              </small>
            </div>
          </div>
        )}

        <Input
          className="d-none"
          invalid={fieldState?.error || error}
          type="file"
          placeholder={placeholder}
          innerRef={fileInputRef}
          isRequired={isRequired}
          name={name}
          accept={accept}
          onChange={(e) => handleImageChange(e.target.files[0])}
        />
      </div>

      {(fieldState?.error || error) && (
        <Label className="label-text text-danger">
          {fieldState?.error?.message || error}
        </Label>
      )}
    </FormGroup>
  );
});

export default ImageInputField;
